import { createAction } from "@reduxjs/toolkit";
import { call, put, takeEvery } from "redux-saga/effects";
import axios from "axios";
import {
  FETCH_EXPERIENCES_STORYBOARD_LISTS_REQUEST,
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_SUCCEEDED,
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_FAILED,
  CREATE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  CREATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  CREATING_EXPERIENCES_STORYBOARD_LIST_FAILED,
  RESET_EXPERIENCES_STORYBOARD_LIST_CREATE,
  UPDATE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  UPDATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  UPDATING_EXPERIENCES_STORYBOARD_LIST_FAILED,
  DELETE_EXPERIENCES_STORYBOARD_LIST_REQUEST,
  DELETING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED,
  DELETING_EXPERIENCES_STORYBOARD_LIST_FAILED,
} from "../actionTypes";
import { StoryboardListItem } from "src/types/experiences/storyboardLists";

const cleverstoryApiUrl = process.env.REACT_APP_CLEVERSTORY_API_URL;
const STORYBOARD_LISTS_URL = `${cleverstoryApiUrl}/api/1.0/storyboards/list`;

// ─── Fetch All ────────────────────────────────────────────────────────────────

export const fetchExperiencesStoryboardLists = createAction(
  FETCH_EXPERIENCES_STORYBOARD_LISTS_REQUEST
);

const fetchingExperiencesStoryboardListsSucceeded = createAction<StoryboardListItem[]>(
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_SUCCEEDED
);
const fetchingExperiencesStoryboardListsFailed = createAction<any>(
  FETCHING_EXPERIENCES_STORYBOARD_LISTS_FAILED
);

const fetchExperiencesStoryboardListsSaga = function* (): any {
  const fetchStoryboardListsAPI = () =>
    axios.get(STORYBOARD_LISTS_URL).then((r) => r.data);

  try {
    const response = yield call(fetchStoryboardListsAPI);
    yield put(fetchingExperiencesStoryboardListsSucceeded(response));
  } catch (error) {
    yield put(fetchingExperiencesStoryboardListsFailed({ error }));
  }
};

// ─── Create ───────────────────────────────────────────────────────────────────

export const createExperiencesStoryboardList = createAction<{ name: string }>(
  CREATE_EXPERIENCES_STORYBOARD_LIST_REQUEST
);

const creatingExperiencesStoryboardListSucceeded = createAction<StoryboardListItem>(
  CREATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED
);
const creatingExperiencesStoryboardListFailed = createAction<any>(
  CREATING_EXPERIENCES_STORYBOARD_LIST_FAILED
);

// Clears the create state after its post-create navigation has been consumed,
// so it can't re-fire the redirect on a later remount (mirrors collections'
// invalidateCollectionListCreationState).
export const resetExperiencesStoryboardListCreate = createAction(
  RESET_EXPERIENCES_STORYBOARD_LIST_CREATE
);

const createExperiencesStoryboardListSaga = function* (
  action: ReturnType<typeof createExperiencesStoryboardList>
): any {
  const createStoryboardListAPI = () =>
    axios.post(STORYBOARD_LISTS_URL, action.payload).then((r) => r.data);

  try {
    const response = yield call(createStoryboardListAPI);
    yield put(creatingExperiencesStoryboardListSucceeded(response));
  } catch (error) {
    yield put(creatingExperiencesStoryboardListFailed({ error }));
  }
};

// ─── Update (rename) ───────────────────────────────────────────────────────────

export const updateExperiencesStoryboardList = createAction<{
  id: string;
  name?: string;
  global?: boolean;
}>(UPDATE_EXPERIENCES_STORYBOARD_LIST_REQUEST);

const updatingExperiencesStoryboardListSucceeded = createAction<StoryboardListItem>(
  UPDATING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED
);
const updatingExperiencesStoryboardListFailed = createAction<{ id: string; error: any }>(
  UPDATING_EXPERIENCES_STORYBOARD_LIST_FAILED
);

const updateExperiencesStoryboardListSaga = function* (
  action: ReturnType<typeof updateExperiencesStoryboardList>
): any {
  const { id, name, global } = action.payload;

  const body: { name?: string; global?: boolean } = {};
  if (name !== undefined) body.name = name;
  if (global !== undefined) body.global = global;

  const updateStoryboardListAPI = () =>
    axios.put(`${STORYBOARD_LISTS_URL}/${id}`, body).then((r) => r.data);

  try {
    const response = yield call(updateStoryboardListAPI);
    yield put(updatingExperiencesStoryboardListSucceeded(response));
  } catch (error) {
    yield put(updatingExperiencesStoryboardListFailed({ id, error }));
  }
};

// ─── Delete ─────────────────────────────────────────────────────────────────────

export const deleteExperiencesStoryboardList = createAction<{ id: string }>(
  DELETE_EXPERIENCES_STORYBOARD_LIST_REQUEST
);

const deletingExperiencesStoryboardListSucceeded = createAction<{ id: string }>(
  DELETING_EXPERIENCES_STORYBOARD_LIST_SUCCEEDED
);
const deletingExperiencesStoryboardListFailed = createAction<{ id: string; error: any }>(
  DELETING_EXPERIENCES_STORYBOARD_LIST_FAILED
);

const deleteExperiencesStoryboardListSaga = function* (
  action: ReturnType<typeof deleteExperiencesStoryboardList>
): any {
  const { id } = action.payload;

  const deleteStoryboardListAPI = () =>
    axios.delete(`${STORYBOARD_LISTS_URL}/${id}`).then((r) => r.data);

  try {
    yield call(deleteStoryboardListAPI);
    yield put(deletingExperiencesStoryboardListSucceeded({ id }));
  } catch (error) {
    yield put(deletingExperiencesStoryboardListFailed({ id, error }));
  }
};

// ─── Root Saga ────────────────────────────────────────────────────────────────

export default function* storyboardListsSaga() {
  yield takeEvery(FETCH_EXPERIENCES_STORYBOARD_LISTS_REQUEST, fetchExperiencesStoryboardListsSaga);
  yield takeEvery(CREATE_EXPERIENCES_STORYBOARD_LIST_REQUEST, createExperiencesStoryboardListSaga);
  yield takeEvery(UPDATE_EXPERIENCES_STORYBOARD_LIST_REQUEST, updateExperiencesStoryboardListSaga);
  yield takeEvery(DELETE_EXPERIENCES_STORYBOARD_LIST_REQUEST, deleteExperiencesStoryboardListSaga);
}
